import {
  liftContact,
  schools,
  type School,
  type Student,
} from "./data";
import { SYSTEMS_INBOX, staffNoticeRecipients } from "./email/outbound";
import type { Locale } from "./locale";

export const MAIL_STORAGE_KEY = "lift-app-mail-v1";
export const DEFAULT_STAFF_INBOX = SYSTEMS_INBOX;

export type RegistrationMailReason = "registered" | "child_added";

export type MailKind =
  | "family_welcome"
  | "staff_registration"
  | "event_confirmation"
  | "staff_rsvp";

export type MailMessage = {
  id: string;
  kind: MailKind;
  to: string[];
  from: string;
  subject: string;
  body: string;
  createdAt: string;
  studentId: string;
  eventId?: string;
};

export type NotificationSettings = {
  centralEmail: string;
  usePerSchool: boolean;
  schoolEmails: Record<School, string>;
};

export type MailState = {
  settings: NotificationSettings;
  messages: MailMessage[];
};

export function defaultSchoolEmails(): Record<School, string> {
  return {
    "Suttons Bay": "suttonsbay@lift.test",
    Northport: "northport@lift.test",
    Leland: "leland@lift.test",
    "Glen Lake": "glenlake@lift.test",
  };
}

export function defaultNotificationSettings(): NotificationSettings {
  return {
    centralEmail: DEFAULT_STAFF_INBOX,
    usePerSchool: false,
    schoolEmails: defaultSchoolEmails(),
  };
}

export function emptyMailState(): MailState {
  return {
    settings: defaultNotificationSettings(),
    messages: [],
  };
}

export function normalizeEmailList(values: string[]): string[] {
  const seen = new Set<string>();
  const next: string[] = [];
  values.forEach((value) => {
    const email = value.trim().toLowerCase();
    if (!email || seen.has(email)) return;
    seen.add(email);
    next.push(email);
  });
  return next;
}

export function staffRecipients(
  settings: NotificationSettings,
  school: School,
): string[] {
  return staffNoticeRecipients({
    centralEmail: settings.centralEmail,
    usePerSchool: settings.usePerSchool,
    schoolEmail: settings.schoolEmails[school] ?? "",
  });
}

export function familyWelcomeCopy(
  student: Student,
  locale: Locale,
  reason: RegistrationMailReason = "registered",
): { subject: string; body: string } {
  if (reason === "child_added") {
    if (locale === "es") {
      return {
        subject: `${student.given} se agregó a su familia en Lift`,
        body: [
          `${student.given} ${student.family} se agregó a su cuenta familiar de Lift.`,
          "",
          `Escuela: ${student.school}`,
          `Grado: ${student.grade}`,
          "",
          "El formulario de este año quedó archivado. No tiene que llenarlo otra vez para unirse a un viaje. Inicie sesión para ver los eventos de su escuela.",
          "",
          "Contacto de Lift:",
          liftContact.name,
          liftContact.email,
          liftContact.phone,
        ].join("\n"),
      };
    }
    return {
      subject: `${student.given} was added to your Lift family`,
      body: [
        `${student.given} ${student.family} was added to your Lift family account.`,
        "",
        `School: ${student.school}`,
        `Grade: ${student.grade}`,
        "",
        "This year's form is on file. You do not fill it again to join a trip. Sign in to see events for your school.",
        "",
        "Lift contact:",
        liftContact.name,
        liftContact.email,
        liftContact.phone,
      ].join("\n"),
    };
  }
  if (locale === "es") {
    return {
      subject: `Felicitaciones, ${student.given} ya está inscrito en Lift`,
      body: [
        `Felicitaciones, ${student.given} ${student.family} ya está inscrito en Lift.`,
        "",
        `Escuela: ${student.school}`,
        `Grado: ${student.grade}`,
        "",
        "El formulario de este año quedó archivado. No tiene que llenarlo otra vez para unirse a un viaje. Inicie sesión para ver los eventos de su escuela.",
        "",
        "Contacto de Lift:",
        liftContact.name,
        liftContact.email,
        liftContact.phone,
        "",
        "Si tiene más de un estudiante en edad Lift, puede agregarlos a la misma cuenta familiar.",
      ].join("\n"),
    };
  }
  return {
    subject: `Congratulations, ${student.given} is registered with Lift`,
    body: [
      `Congratulations, ${student.given} ${student.family} is registered with Lift.`,
      "",
      `School: ${student.school}`,
      `Grade: ${student.grade}`,
      "",
      "This year's form is on file. You do not fill it again to join a trip. Sign in to see events for your school.",
      "",
      "Lift contact:",
      liftContact.name,
      liftContact.email,
      liftContact.phone,
      "",
      "If you have more than one Lift-age student, you can add them under the same family account.",
    ].join("\n"),
  };
}

export function eventConfirmationCopy(
  student: Student,
  event: { name: string; dateLabel: string; timeLabel: string; location: string },
  status: "registered" | "waitlisted",
  locale: Locale,
  promoted = false,
): { subject: string; body: string } {
  const when = [event.dateLabel, event.timeLabel].filter(Boolean).join(" · ");
  if (promoted) {
    if (locale === "es") {
      return {
        subject: `Se abrió un lugar: ${event.name}`,
        body: [
          `Se abrió un lugar. ${student.given} ya está inscrito para ${event.name}.`,
          "",
          when,
          event.location,
          "",
          "Le escribimos a este tutor porque el estudiante quedó registrado desde la cuenta familiar.",
          "",
          "Contacto de Lift:",
          liftContact.name,
          liftContact.email,
          liftContact.phone,
        ].join("\n"),
      };
    }
    return {
      subject: `A spot opened: ${event.name}`,
      body: [
        `A spot opened. ${student.given} is now registered for ${event.name}.`,
        "",
        when,
        event.location,
        "",
        "This note goes to the guardian email on the student record.",
        "",
        "Lift contact:",
        liftContact.name,
        liftContact.email,
        liftContact.phone,
      ].join("\n"),
    };
  }
  if (locale === "es") {
    const line =
      status === "waitlisted"
        ? `${student.given} está en la lista de espera para ${event.name}.`
        : `${student.given} está inscrito para ${event.name}.`;
    return {
      subject:
        status === "waitlisted"
          ? `Lista de espera: ${event.name}`
          : `Confirmado: ${event.name}`,
      body: [
        line,
        "",
        when,
        event.location,
        "",
        "Le escribimos a este tutor porque el estudiante quedó registrado desde la cuenta familiar.",
        "",
        "Contacto de Lift:",
        liftContact.name,
        liftContact.email,
        liftContact.phone,
      ].join("\n"),
    };
  }
  const line =
    status === "waitlisted"
      ? `${student.given} is on the waitlist for ${event.name}.`
      : `${student.given} is registered for ${event.name}.`;
  return {
    subject:
      status === "waitlisted"
        ? `Waitlist: ${event.name}`
        : `Confirmed: ${event.name}`,
    body: [
      line,
      "",
      when,
      event.location,
      "",
      "This note goes to the guardian email on the student record.",
      "",
      "Lift contact:",
      liftContact.name,
      liftContact.email,
      liftContact.phone,
    ].join("\n"),
  };
}

export function staffRsvpCopy(
  student: Student,
  eventName: string,
  status: "registered" | "waitlisted",
  promoted = false,
): { subject: string; body: string } {
  if (promoted) {
    return {
      subject: `Off the waitlist: ${student.given} ${student.family} · ${eventName}`,
      body: [
        `${student.given} ${student.family} moved off the waitlist and is now registered for ${eventName}.`,
        "",
        `School: ${student.school}`,
        `Guardian: ${student.guardianName} · ${student.guardianEmail}`,
      ].join("\n"),
    };
  }
  const word = status === "waitlisted" ? "waitlisted" : "registered";
  return {
    subject: `RSVP ${word}: ${student.given} ${student.family} · ${eventName}`,
    body: [
      `${student.given} ${student.family} is ${word} for ${eventName}.`,
      "",
      `School: ${student.school}`,
      `Guardian: ${student.guardianName} · ${student.guardianEmail}`,
    ].join("\n"),
  };
}

export function staffRegistrationCopy(
  student: Student,
  reason: RegistrationMailReason = "registered",
): {
  subject: string;
  body: string;
} {
  if (reason === "child_added") {
    return {
      subject: `Child added: ${student.given} ${student.family}`,
      body: [
        `${student.given} ${student.family} was added to an existing Lift family.`,
        "",
        `School: ${student.school}`,
        `Grade: ${student.grade}`,
        `Student email: ${student.email}`,
        `Guardian: ${student.guardianName} · ${student.guardianEmail} · ${student.guardianPhone}`,
      ].join("\n"),
    };
  }
  return {
    subject: `New Lift registration: ${student.given} ${student.family}`,
    body: [
      `${student.given} ${student.family} just submitted this year's form.`,
      "",
      `School: ${student.school}`,
      `Grade: ${student.grade}`,
      `Student email: ${student.email}`,
      `Guardian: ${student.guardianName} · ${student.guardianEmail} · ${student.guardianPhone}`,
      "",
      "This is the signal to send a welcome note, or treat the family confirmation email as the welcome.",
    ].join("\n"),
  };
}

function mailId(): string {
  return `mail-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
}

export function sendRegistrationMail(
  state: MailState,
  student: Student,
  locale: Locale,
  now = new Date().toISOString(),
  reason: RegistrationMailReason = "registered",
): { state: MailState; family: MailMessage; staff: MailMessage | null } {
  const familyCopy = familyWelcomeCopy(student, locale, reason);
  const family: MailMessage = {
    id: mailId(),
    kind: "family_welcome",
    to: normalizeEmailList([student.guardianEmail, student.email]),
    from: liftContact.email,
    subject: familyCopy.subject,
    body: familyCopy.body,
    createdAt: now,
    studentId: student.id,
  };

  const staffTo = staffRecipients(state.settings, student.school);
  const staffCopy = staffRegistrationCopy(student, reason);
  const staff: MailMessage | null =
    staffTo.length === 0
      ? null
      : {
          id: mailId(),
          kind: "staff_registration",
          to: staffTo,
          from: DEFAULT_STAFF_INBOX,
          subject: staffCopy.subject,
          body: staffCopy.body,
          createdAt: now,
          studentId: student.id,
        };

  return {
    state: {
      ...state,
      messages: staff ? [family, staff, ...state.messages] : [family, ...state.messages],
    },
    family,
    staff,
  };
}

export function latestFamilyMail(
  messages: MailMessage[],
  studentId: string,
): MailMessage | undefined {
  return messages.find(
    (item) => item.kind === "family_welcome" && item.studentId === studentId,
  );
}

export type EventRegistrationMailInput = {
  id: string;
  name: string;
  dateLabel: string;
  timeLabel: string;
  location: string;
};

/**
 * Records an event RSVP for the guardian and for staff.
 * Nothing is transmitted here. Delivery stays in the email service.
 */
export function sendEventRegistrationMail(
  state: MailState,
  student: Student,
  event: EventRegistrationMailInput,
  status: "registered" | "waitlisted",
  locale: Locale,
  now = new Date().toISOString(),
  promoted = false,
): { state: MailState; family: MailMessage | null; staff: MailMessage | null } {
  if (!student.guardianEmail.trim()) {
    return { state, family: null, staff: null };
  }

  const familyCopy = eventConfirmationCopy(student, event, status, locale, promoted);
  const family: MailMessage = {
    id: mailId(),
    kind: "event_confirmation",
    to: normalizeEmailList([student.guardianEmail]),
    from: liftContact.email,
    subject: familyCopy.subject,
    body: familyCopy.body,
    createdAt: now,
    studentId: student.id,
    eventId: event.id,
  };

  const staffTo = staffRecipients(state.settings, student.school);
  const staffCopy = staffRsvpCopy(student, event.name, status, promoted);
  const staff: MailMessage | null =
    staffTo.length === 0
      ? null
      : {
          id: mailId(),
          kind: "staff_rsvp",
          to: staffTo,
          from: DEFAULT_STAFF_INBOX,
          subject: staffCopy.subject,
          body: staffCopy.body,
          createdAt: now,
          studentId: student.id,
          eventId: event.id,
        };

  return {
    state: {
      ...state,
      messages: staff ? [family, staff, ...state.messages] : [family, ...state.messages],
    },
    family,
    staff,
  };
}

export function latestEventMail(
  messages: MailMessage[],
  studentId: string,
  eventId: string,
): MailMessage | undefined {
  return messages.find(
    (item) =>
      item.kind === "event_confirmation" &&
      item.studentId === studentId &&
      item.eventId === eventId,
  );
}

export function staffInbox(messages: MailMessage[]): MailMessage[] {
  return messages.filter(
    (item) => item.kind === "staff_registration" || item.kind === "staff_rsvp",
  );
}

export function normalizeSettings(
  settings: Partial<NotificationSettings> | undefined,
): NotificationSettings {
  const defaults = defaultNotificationSettings();
  const schoolEmails = { ...defaults.schoolEmails };
  schools.forEach((school) => {
    const value = settings?.schoolEmails?.[school];
    if (typeof value === "string") schoolEmails[school] = value;
  });
  return {
    centralEmail: settings?.centralEmail?.trim() || defaults.centralEmail,
    usePerSchool: Boolean(settings?.usePerSchool),
    schoolEmails,
  };
}

export function formatMailTime(iso: string, locale: Locale): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(locale === "es" ? "es-US" : "en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}
